import { i18nContainer, utils, enumContainer } from '@wallaby-ui/core';
import { isEmpty } from 'lodash';
import { Layout, PlotData, PlotHoverEvent, PlotMouseEvent } from 'plotly.js';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { usePlotlyLocaleInit } from 'src/js/hooks';
import { ChartReportConfig } from 'src/js/model/reports_config';
import { useAppDispatch, useAppSelector } from 'src/js/store_hooks';

import Chart from './Chart';
import { CHART_REDUCER_STATE_ID } from '../chart_constants';
import { getChartConfig } from '../chart_selectors';
import { loadChartConfig } from '../chart_thunks';
import { Box } from '@mui/material';

interface OwnProps {
  configPath?: string;
  reportData: Record<string, any>;
  chartId: string;
  onClick?: (event: PlotMouseEvent) => void;
  onClickYTick?: (data: string) => void;
  onHover?: (event: PlotHoverEvent) => void;
  onUnhover?: (event: PlotMouseEvent) => void;
  propsChartConfigModel?: ChartReportConfig;
  downloadImageTitle?: string;
  additionalLayoutConfig?: Partial<Layout>;
  additionalDataConfig?: Partial<PlotData>[];
}

type Props = OwnProps;

const ChartContainer: React.FC<Props> = (props) => {
  const {
    configPath,
    reportData,
    chartId,
    onClick,
    onClickYTick,
    onHover,
    onUnhover,
    propsChartConfigModel,
    downloadImageTitle,
    additionalLayoutConfig,
    additionalDataConfig,
    ...restProps
  } = props;
  const reducerId = utils.reducerUtils.getInstanceReducerId(
    CHART_REDUCER_STATE_ID,
    chartId
  );

  const chartConfig = useAppSelector((state) => getChartConfig(state, props));
  const currentLocale = useAppSelector(
    (state) => state[i18nContainer.I18N_REDUCER_STATE_ID].currentLocale
  );
  const i18nConfig = useAppSelector(
    (state) => state[i18nContainer.I18N_REDUCER_STATE_ID].i18nConfig
  );
  const enumMap = useAppSelector(
    (state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap
  );

  const dispatch = useAppDispatch();
  usePlotlyLocaleInit(i18nConfig, currentLocale);

  const chartConfigModel = useMemo<ChartReportConfig>(() => {
    return (
      propsChartConfigModel ?? new ChartReportConfig(chartConfig, configPath)
    );
  }, [chartConfig, configPath, propsChartConfigModel]);

  useEffect(() => {
    if (!chartConfig && !propsChartConfigModel) {
      dispatch(loadChartConfig(reducerId, configPath));
    }
  }, [reducerId, configPath, dispatch, chartConfig, propsChartConfigModel]);

  return chartConfigModel && chartConfig && !isEmpty(i18nConfig) ? (
    <Box color="#E2E8F2" borderRadius="8px" border="1px solid" padding="2px">
      <Chart
        {...restProps}
        reportConfig={chartConfigModel}
        reportData={reportData}
        enumMap={enumMap}
        onClick={onClick}
        onClickYTick={onClickYTick}
        onHover={onHover}
        onUnhover={onUnhover}
        downloadImageTitle={downloadImageTitle}
        additionalLayoutConfig={additionalLayoutConfig}
        additionalDataConfig={additionalDataConfig}
      />
    </Box>
  ) : null;
};

export default ChartContainer;
